/**
 * Maintenance environment configuration.
 *
 * Maintenance is intentionally controlled in code instead of the database so the
 * real public domain stays protected while Lovable previews, staging, and local
 * development remain fully usable.
 */

/** Master switch for the coming-soon page. */
const MAINTENANCE_MODE = false;

/** When true, only exact live hosts are locked behind maintenance. */
const MAINTENANCE_MODE_PRODUCTION_ONLY = true;

/** Exact public hostnames that should always show the maintenance page. */
const LIVE_MAINTENANCE_HOSTS = [
  "padelcorner.lv",
  "www.padelcorner.lv",
] as const;

/** Preview, editor, staging, and local hosts that must always bypass maintenance. */
const MAINTENANCE_BYPASS_HOST_PATTERNS = [
  "-preview--",
  "localhost",
  "127.0.0.1",
] as const;

function getCurrentHost(): string {
  return window.location.hostname.toLowerCase();
}

export function isMaintenanceBypassedHost(host = getCurrentHost()): boolean {
  return MAINTENANCE_BYPASS_HOST_PATTERNS.some((pattern) => host.includes(pattern));
}

/**
 * Returns true only for exact live domains that should stay in maintenance.
 */
export function isProductionHost(host = getCurrentHost()): boolean {
  return LIVE_MAINTENANCE_HOSTS.includes(host as (typeof LIVE_MAINTENANCE_HOSTS)[number]);
}

/**
 * Host-based maintenance gate:
 * - padelcorner.lv / www => maintenance page
 * - Lovable preview, localhost, staging => full site
 */
export function shouldShowMaintenancePage(host = getCurrentHost()): boolean {
  if (!MAINTENANCE_MODE) return false;
  if (isMaintenanceBypassedHost(host)) return false;
  if (MAINTENANCE_MODE_PRODUCTION_ONLY) return isProductionHost(host);
  return true;
}

export const maintenanceConfig = {
  enabled: MAINTENANCE_MODE,
  productionOnly: MAINTENANCE_MODE_PRODUCTION_ONLY,
  liveHosts: [...LIVE_MAINTENANCE_HOSTS],
  bypassHostPatterns: [...MAINTENANCE_BYPASS_HOST_PATTERNS],
} as const;
